import { apiConfig } from "~/config/api";
import {
  forceRefreshTokens,
  getAccessToken,
  initiateLogin,
} from "~/features/auth/services/keycloakService";

import { armRequestTimeout, combineAbortSignals } from "./requestTimeout";

/**
 * When a request is still 401 after a refresh attempt, tokens are effectively
 * dead — re-auth is the only path forward. Kick the user to the login flow.
 * Debounced so parallel failing requests don't each fire their own redirect.
 */
let authRedirectInFlight = false;
const redirectToLogin = () => {
  if (authRedirectInFlight) {
    return;
  }
  authRedirectInFlight = true;
  // initiateLogin returns a promise that never resolves (sets window.location),
  // so we fire-and-forget here. Errors bubble as a last-resort console warn.
  void initiateLogin().catch((err) => {
    authRedirectInFlight = false;
    console.warn("Failed to initiate re-auth", err);
  });
};

export interface ApiRequestContext {
  /**
   * Workflow session enrollment uses a short-lived HttpOnly browser cookie.
   * All other API calls deliberately omit cookies.
   */
  credentials?: RequestCredentials;
  tenantId?: string | null;
  userProfileId?: string | null;
  securityContext?: string | null;
  view?: string;
  /**
   * When false, the `x-view` header is NOT sent. Default is true.
   * Useful for record-service calls, where the BE ignores `x-view` for BOS data
   * anyway.
   */
  sendView?: boolean;
  /**
   * When false, omit `x-api-key` / `x-cpes-service-name`. Default is true.
   *
   * Flavour-provisioning calls still send credentials, but via the dedicated
   * provisioner identity in `custom-fetch` (not this flag). Prefer
   * `VITE_VERTICAL_PROVISIONER_*` when talking to a local compose that only
   * registers `cpes-record-service` + `DUMMY-123` while `VITE_API_KEY` is a
   * cloud `cpes-admin-portal` key.
   */
  sendServiceCredentials?: boolean;
}

interface ApiErrorOptions {
  message: string;
  status?: number;
  code?: string;
  method?: string;
  url?: string;
  /** `Location` / `Content-Location` on the error response, when the write persisted. */
  location?: string;
  body?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly method?: string;
  readonly url?: string;
  readonly location?: string;
  readonly body?: unknown;

  constructor({ message, status, code, method, url, location, body, cause }: ApiErrorOptions) {
    super(message, { cause });
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.method = method;
    this.url = url;
    this.location = location;
    this.body = body;
  }
}

/**
 * Record-service (and other BE adapters) wrap payloads in `{ data: T }`. Error
 * responses follow the same envelope — e.g. delete-guard 409s carry RFC 7807
 * fields (`detail`, `title`, `guard`) on the inner object, not the root.
 */
export const unwrapApiEnvelope = (body: unknown): unknown => {
  if (!body || typeof body !== "object" || !("data" in body)) {
    return body;
  }

  const inner = (body as Record<string, unknown>).data;

  return inner ?? body;
};

const extractErrorMessage = (body: unknown, fallback: string): string => {
  const payload = unwrapApiEnvelope(body);
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const errorBody = payload as Record<string, unknown>;

  const messageCandidates = ["message", "error", "detail", "title"] as const;
  for (const key of messageCandidates) {
    const value = errorBody[key];

    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return fallback;
};

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

export const hasApiErrorStatus = (error: unknown, status: number): boolean =>
  isApiError(error) && error.status === status;

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

/**
 * Local compose / outbox flakiness: record-service often persists the row, then
 * returns 500 because the completion event could not be published. Kafka and
 * SNS ("Topic does not exist") are the two messages we see on local compose.
 * Callers that can re-read the created entity should treat this as a soft
 * success rather than a user-visible failure.
 */
const OUTBOX_PUBLISH_FAILED =
  /completion event emission failed|topic does not exist \(service:\s*sns\b/i;

export const isCreatedButEmissionFailed = (error: unknown): boolean => {
  const candidates: string[] = [];

  if (error instanceof Error && error.message.trim()) {
    candidates.push(error.message);
  }

  if (isApiError(error) && error.body != null) {
    candidates.push(extractErrorMessage(error.body, ""));
    if (typeof error.body === "string") {
      candidates.push(error.body);
    }
  }

  return candidates.some((text) => OUTBOX_PUBLISH_FAILED.test(text));
};

// --- Internal helpers (module-private) ---

const getHeaders = async (
  requestContext?: ApiRequestContext,
  body?: BodyInit | null,
): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};

  if (!(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const accessToken = await getAccessToken();
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (requestContext?.userProfileId) {
    headers["x-user-profile-id"] = requestContext.userProfileId;
  }

  if (requestContext?.tenantId) {
    headers["x-tenant-id"] = requestContext.tenantId;
    headers["x-security-context"] = requestContext.securityContext || requestContext.tenantId;
  }

  if (requestContext?.sendServiceCredentials !== false) {
    if (apiConfig.apiKey) {
      headers["x-api-key"] = apiConfig.apiKey;
    }

    if (apiConfig.serviceName) {
      headers["x-cpes-service-name"] = apiConfig.serviceName;
    }
  }

  if (requestContext?.sendView !== false) {
    headers["x-view"] = requestContext?.view ?? "default";
  }

  return headers;
};

/**
 * Core request execution with timeout and automatic 401 token refresh.
 */
const executeRequest = async (
  baseUrl: string,
  endpoint: string,
  options: RequestInit = {},
  requestContext?: ApiRequestContext,
  timeoutMs: number = apiConfig.timeout,
): Promise<{ response: Response; url: string }> => {
  const url = `${baseUrl}${endpoint}`;
  const callerSignal = options.signal;
  const timeoutAbort = armRequestTimeout(timeoutMs);
  const headers = await getHeaders(requestContext, options.body);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers: { ...headers, ...options.headers },
      signal: combineAbortSignals(callerSignal, timeoutAbort.signal),
      // All auth is via Authorization header — never send cookies.
      // Prevents HttpOnly cookies (e.g. Keycloak session) from bloating
      // request headers past upstream limits (431).
      credentials: requestContext?.credentials ?? "omit",
    });
  } catch (error) {
    // A caller-initiated abort is not a failure: rethrow it untouched so
    // TanStack recognises its own cancellation. Wrapping it in a 408 would
    // raise a "Request timed out" toast on every navigation away.
    if (callerSignal?.aborted) {
      throw error;
    }

    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError({
        message: `Request timed out after ${timeoutMs}ms`,
        status: 408,
        code: "TIMEOUT",
        method: options.method,
        url,
        cause: error,
      });
    }

    throw new ApiError({
      message: "Network request failed",
      code: "NETWORK_ERROR",
      method: options.method,
      url,
      cause: error,
    });
  } finally {
    timeoutAbort.clear();
  }

  if (response.status !== 401) {
    return { response, url };
  }

  const originalAuth = headers.Authorization;
  let accessToken = await getAccessToken();

  // No session — user must sign in.
  if (!accessToken) {
    redirectToLogin();
    return { response, url };
  }

  let refreshedAuth = `Bearer ${accessToken}`;

  // Token is still valid by expiry but the resource server rejected it — try an
  // explicit refresh (stale roles) before giving up.
  if (refreshedAuth === originalAuth) {
    const forced = await forceRefreshTokens();
    if (forced?.access_token) {
      accessToken = forced.access_token;
      refreshedAuth = `Bearer ${accessToken}`;
    }
  }

  // Cannot obtain a different token — this 401 is tenant/permission/config, not
  // an expired session. Return it to callers; do NOT redirect to Keycloak (SSO
  // would silently re-auth the same user → infinite loop).
  if (refreshedAuth === originalAuth) {
    return { response, url };
  }

  headers.Authorization = refreshedAuth;

  const retryTimeout = armRequestTimeout(timeoutMs);
  try {
    const retriedResponse = await fetch(url, {
      ...options,
      headers: { ...headers, ...options.headers },
      signal: combineAbortSignals(options.signal, retryTimeout.signal),
    });

    // Still 401 after a genuinely new token — re-login will not help (same SSO
    // session). Surface the error to the UI instead of redirect-looping.
    return { response: retriedResponse, url };
  } catch (error) {
    if (options.signal?.aborted) {
      throw error;
    }

    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError({
        message: `Request timed out after ${timeoutMs}ms (retry)`,
        status: 408,
        code: "TIMEOUT",
        method: options.method,
        url,
        cause: error,
      });
    }

    throw new ApiError({
      message: "Network request failed (retry)",
      code: "NETWORK_ERROR",
      method: options.method,
      url,
      cause: error,
    });
  } finally {
    retryTimeout.clear();
  }
};

const HEADER_TOO_LARGE_MESSAGE =
  "Request headers are too large for the local service (HTTP 431). " +
  "Restart `npm run dev` (512KB header limit), clear localhost cookies / sign in again, then retry.";

const assertOk = async (response: Response, method?: string, url?: string): Promise<void> => {
  if (response.ok) {
    return;
  }

  if (response.status === 431) {
    throw new ApiError({
      message: HEADER_TOO_LARGE_MESSAGE,
      status: 431,
      method,
      url,
    });
  }

  let body: unknown;
  let message = `HTTP ${response.status}`;

  try {
    const text = await response.text();

    if (text) {
      try {
        body = JSON.parse(text);
        message = extractErrorMessage(body, message);
      } catch {
        body = text;
        // Non-JSON error bodies are usually HTML error pages from a proxy/gateway
        // (e.g. a 404/502 "<html>…Resource not found…</html>"). Dumping raw markup
        // into a toast is noise, so keep the readable "HTTP <status>" line and only
        // promote short, non-HTML plain text.
        const trimmed = text.trim();
        const looksLikeHtml = trimmed.startsWith("<");

        if (!looksLikeHtml && trimmed.length > 0 && trimmed.length <= 200) {
          message = trimmed;
        }
      }
    }
  } catch {
    body = undefined;
  }

  throw new ApiError({
    message,
    status: response.status,
    method,
    url,
    location:
      response.headers.get("Location") ?? response.headers.get("Content-Location") ?? undefined,
    body,
  });
};

/**
 * Internal: execute request, assert ok, parse JSON.
 *
 * SAFETY: Returns `undefined as T` for 204 and empty-body responses.
 * This is a deliberate type-level lie — callers receiving `T` may get
 * `undefined` at runtime. Void endpoints should use `apiMakeRawRequest`
 * instead. Callers that care must null-check the result.
 */
const makeRequest = async <T>(
  baseUrl: string,
  endpoint: string,
  options: RequestInit = {},
  unwrapData = true,
  requestContext?: ApiRequestContext,
): Promise<T> => {
  const { response } = await executeRequest(baseUrl, endpoint, options, requestContext);
  await assertOk(response, options.method, `${baseUrl}${endpoint}`);

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  const json = JSON.parse(text);

  if (unwrapData && json && typeof json === "object" && "data" in json) {
    return json.data;
  }

  return json;
};

// --- Public API ---

export const apiGet = async <T>(
  baseUrl: string,
  endpoint: string,
  unwrapData = true,
  extraHeaders?: Record<string, string>,
  requestContext?: ApiRequestContext,
  /** Caller's cancellation, combined with the request's timeout budget. Pass
   *  the `queryFn` signal so an abandoned page's reads stop at the network. */
  signal?: AbortSignal,
): Promise<T> =>
  makeRequest<T>(
    baseUrl,
    endpoint,
    { method: "GET", headers: extraHeaders, ...(signal ? { signal } : {}) },
    unwrapData,
    requestContext,
  );

export const apiPost = async <T>(
  baseUrl: string,
  endpoint: string,
  data: unknown,
  extraHeaders?: Record<string, string>,
  requestContext?: ApiRequestContext,
  /** See {@link apiGet}. */
  signal?: AbortSignal,
): Promise<T> =>
  makeRequest<T>(
    baseUrl,
    endpoint,
    {
      method: "POST",
      body: JSON.stringify(data),
      headers: extraHeaders,
      ...(signal ? { signal } : {}),
    },
    true,
    requestContext,
  );

/**
 * Execute a request and return the raw Response.
 *
 * On non-2xx responses `assertOk` throws (consuming the body), so this
 * function never returns a failed Response. On success, the returned
 * Response has an unconsumed body stream — callers (e.g. `customFetch`)
 * can safely read `.text()` / `.blob()` on it.
 */
export interface ApiRawRequestOptions {
  /** When false, non-2xx responses are returned instead of throwing. Default: true. */
  throwOnError?: boolean;
  /**
   * Abort budget for this request. Defaults to `apiConfig.timeout` (2min).
   * `0` (or a negative value) disables the abort timer — flavour apply holds
   * the HTTP connection until the provisioner finishes.
   */
  timeoutMs?: number;
}

export const apiMakeRawRequest = async (
  baseUrl: string,
  endpoint: string,
  options: RequestInit = {},
  requestContext?: ApiRequestContext,
  rawOptions?: ApiRawRequestOptions,
): Promise<Response> => {
  const { response, url } = await executeRequest(
    baseUrl,
    endpoint,
    options,
    requestContext,
    rawOptions?.timeoutMs,
  );

  if (rawOptions?.throwOnError !== false) {
    await assertOk(response, options.method, url);
  }

  return response;
};

/**
 * Object-style API client — convenience wrapper around the standalone functions.
 * Used by the documents feature which expects `apiClient.get(...)` style calls.
 */
export const apiClient = {
  get: apiGet,
  post: apiPost,
  delete: <T>(
    baseUrl: string,
    endpoint: string,
    requestContext?: ApiRequestContext,
    extraHeaders?: Record<string, string>,
  ): Promise<T> =>
    makeRequest<T>(
      baseUrl,
      endpoint,
      { method: "DELETE", headers: extraHeaders },
      true,
      requestContext,
    ),
  makeRawRequest: apiMakeRawRequest,
};
